import { FC, useState } from 'react';
import { compact } from 'lodash';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import MuiCheckbox from '@mui/material/Checkbox';
import { styled } from '@mui/material/styles';
import {
  CalendarDay,
  downloadLinkAsFile,
  downloadFiles,
} from 'further-ui/utils';
import { Row, Column } from 'further-ui/components/Layout';
import SearchField from 'further-ui/components/SearchField';
import Avatar from 'further-ui/components/Avatar';
import Table, { Column as TableColumn } from 'further-ui/components/Table';
import FullPageDrawer from 'components/new/FullPageDrawer';
import PageFilters, { Filter } from 'components/new/PageFilters';

const TableContainer = styled(Box)({
  flex: 1,
  minHeight: 0,
  overflowY: 'auto',
});

const Checkbox = styled(MuiCheckbox)({
  padding: 0,
});

type Investor = {
  id: string;
  fullName: string;
};

type Props = {
  firmName: string;
  capitalActionName: string;
  capitalNotice: {
    noticeDate?: CalendarDay;
    investorPdfUploads: Array<{
      investorId: string;
      path: string;
      previewUrl: string;
    }>;
  } | null;
  investors: Array<Investor>;
  onClose: () => void;
};

const DownloadCapitalNotices: FC<Props> = ({
  firmName,
  capitalActionName,
  capitalNotice,
  investors,
  onClose,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedInvestorIds, setSelectedInvestorIds] = useState<Array<string>>(
    [],
  );

  const findInvestorPdfUpload = (investorId: string) =>
    capitalNotice?.investorPdfUploads.find(
      (upload) => upload.investorId === investorId,
    );

  const filteredInvestors = investors.filter((investor) =>
    investor.fullName.toLowerCase().includes(searchQuery.toLowerCase()),
  );
  const selectableInvestors = filteredInvestors.filter(
    (investor) => !!findInvestorPdfUpload(investor.id),
  );
  const allVisibleInvestorsSelected =
    selectableInvestors.length > 0 &&
    selectableInvestors.every((investor) =>
      selectedInvestorIds.includes(investor.id),
    );

  const handleSelectDeselectAll = () => {
    setSelectedInvestorIds(
      allVisibleInvestorsSelected
        ? []
        : selectableInvestors.map((investor) => investor.id),
    );
  };

  const getFilename = (investorId: string) => {
    const investorFullName =
      investors.find((investor) => investor.id === investorId)?.fullName ?? '';

    const parts = compact([
      firmName.split(' ').join('-'),
      'capital-notice',
      capitalNotice?.noticeDate?.toLabel('', 'yyyyMMdd') ?? '',
      investorFullName.split(' ').join('-'),
    ]);

    return `${parts.join('-')}.pdf`.toLowerCase();
  };

  const handleDownload = () => {
    if (!capitalNotice) return;
    if (selectedInvestorIds.length === 0) return;

    if (selectedInvestorIds.length === 1) {
      const investorId = selectedInvestorIds[0];

      const investorPdfUpload = findInvestorPdfUpload(investorId);

      if (!investorPdfUpload) return;

      downloadLinkAsFile(investorPdfUpload.previewUrl, getFilename(investorId));
    } else {
      const files = capitalNotice.investorPdfUploads.filter((upload) =>
        selectedInvestorIds.includes(upload.investorId),
      );

      if (files.length === 0) return;

      downloadFiles(
        files.map((file) => ({
          url: file.previewUrl,
          name: getFilename(file.investorId),
        })),
        `${capitalActionName}.zip`,
      );
    }
  };

  const columns: Array<TableColumn<Investor>> = [
    {
      key: 'investor',
      render: ({ fullName }) => (
        <Row spacing="xs" centered>
          <Avatar color="random" text={fullName} size="lg" />
          <Typography variant="h6">{fullName}</Typography>
        </Row>
      ),
    },
    {
      key: 'actions',
      width: '50px',
      render: (investor) => {
        const isInvestorWithoutPdfUpload = !findInvestorPdfUpload(investor.id);
        const tooltipTitle = isInvestorWithoutPdfUpload
          ? 'This PDF is currently being generated. Please try again in a few moments.'
          : undefined;

        return (
          <Tooltip title={tooltipTitle} placement="bottom-start">
            <span>
              <Checkbox
                checked={selectedInvestorIds.includes(investor.id)}
                disabled={isInvestorWithoutPdfUpload}
                onChange={(e) => {
                  setSelectedInvestorIds((prev) =>
                    e.target.checked
                      ? [...prev, investor.id]
                      : prev.filter((id) => id !== investor.id),
                  );
                }}
              />
            </span>
          </Tooltip>
        );
      },
    },
  ];

  return (
    <FullPageDrawer
      open={true}
      title="Download capital notices"
      onClose={onClose}
      innerScroll
      actions={
        <Row spacing="sm" justify="space-between">
          <Button
            variant="outlined"
            color="primary"
            size="large"
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            size="large"
            onClick={handleDownload}
            disabled={selectedInvestorIds.length === 0}
          >
            Download
          </Button>
        </Row>
      }
    >
      <Column spacing="lg" fullWidth fullHeight>
        <Typography variant="body2">
          Please select one or more investors to download capital notices from.
        </Typography>
        <PageFilters
          justify="space-between"
          actions={
            selectableInvestors.length > 0
              ? [
                  {
                    label: allVisibleInvestorsSelected
                      ? 'Deselect all'
                      : 'Select all',
                    onClick: handleSelectDeselectAll,
                  },
                ]
              : []
          }
        >
          <Filter size="md">
            <SearchField
              fullWidth
              placeholder="Search by investor"
              value={searchQuery}
              onSearch={setSearchQuery}
              allowEmptyQuery
              autoSearchOnDebounce
              autoSearchOnBlur
              debounceTime={1}
            />
          </Filter>
        </PageFilters>
        <TableContainer>
          <Table
            columns={columns}
            rows={filteredInvestors}
            emptyMessage="No investors found."
          />
        </TableContainer>
      </Column>
    </FullPageDrawer>
  );
};

export default DownloadCapitalNotices;
